/**
 * "How much gas?" explorer for the Acid Reactions Lab (CT-INTERACT, chemistry/CLAUDE.md
 * "Interactive calculation standard"). The learner sets the mass of solid, the acid concentration
 * and volume, and how far the reaction has gone; the model works out the amounts, the limiting
 * reagent, the gas amount and its volume at SLC, and what is left over.
 *
 * Reviewed data reused: balanced equations from acidReactions.ts, classroom atomic masses through
 * calculateMolarMass (periodicTableData.ts) and the SLC molar volume 24.8 L/mol (gasLaws.ts).
 * The gas syringe capacity (100 mL) is the usual school gas syringe size.
 * reviewStatus: 'pending-teaching-review'.
 */
import { ACID_REACTIONS, acidById, partnerById, type AcidReaction, type PartnerData, type Vigour } from './acidReactions';
import { calculateMolarMass } from './chemistryCalculations';
import { MOLAR_VOLUMES } from './gasLaws';
import { type ExploreChallenge, type ModelField, type WorkedStep, firstModelError, formatModelNumber } from './modelInputContract';
import { formatSpecies, formatTerms, stripState } from './reactionPredictionData';

export interface AcidGasSetup {
  massG: number;
  concentrationMolL: number;
  volumeMl: number;
  progressPct: number;
}

export type AcidGasInput = keyof AcidGasSetup;

export const ACID_GAS_FIELDS: ModelField<AcidGasInput>[] = [
  { key: 'massG', label: 'Mass of solid', unit: 'g', min: 0.01, max: 2, step: 0.01 },
  { key: 'concentrationMolL', label: 'Acid concentration', unit: 'mol/L', min: 0.1, max: 2, step: 0.05 },
  { key: 'volumeMl', label: 'Acid volume', unit: 'mL', min: 5, max: 100, step: 1 },
  { key: 'progressPct', label: 'Reaction progress', unit: '%', min: 0, max: 100, step: 1 },
];

export const DEFAULT_ACID_GAS_SETUP: AcidGasSetup = { massG: 0.05, concentrationMolL: 1, volumeMl: 50, progressPct: 100 };

/** SLC molar volume (25 °C, 100 kPa) from the reviewed gas-law data. */
export const SLC_MOLAR_VOLUME_L = MOLAR_VOLUMES.SLC.litresPerMol;
/** A standard school gas syringe holds 100 mL; more gas than that is reported, not drawn. */
export const GAS_SYRINGE_CAPACITY_ML = 100;
/** The 3D conical flask is drawn as a 250 mL flask. */
export const FLASK_CAPACITY_ML = 250;

/** Reviewed reactions that release a gas (hydrogen or carbon dioxide). */
export const GAS_REACTIONS: AcidReaction[] = ACID_REACTIONS.filter(
  (reaction) => reaction.pattern === 'salt-hydrogen' || reaction.pattern === 'salt-water-carbon-dioxide',
);

export const DEFAULT_GAS_REACTION_ID = 'HCl-Mg';

export function gasReactionById(id: string): AcidReaction {
  const reaction = GAS_REACTIONS.find((entry) => entry.id === id);
  if (!reaction) throw new Error(`No reviewed gas reaction: ${id}`);
  return reaction;
}

export function gasReactionLabel(reaction: AcidReaction): string {
  const partner = partnerById(reaction.partner).name;
  return `${partner.charAt(0).toUpperCase()}${partner.slice(1)} + ${acidById(reaction.acid).name}`;
}

export type LimitingReagent = 'solid' | 'acid' | 'both';

export interface AcidGasResult {
  reaction: AcidReaction;
  partner: PartnerData;
  gasFormula: 'H2' | 'CO2';
  gasName: string;
  molarMassG: number;
  solidMol: number;
  acidMol: number;
  limiting: LimitingReagent;
  /** Gas made so far (at the chosen progress). */
  gasMol: number;
  gasMl: number;
  /** Gas made when the reaction has finished, for the scale of the readout. */
  finalGasMl: number;
  solidLeftG: number;
  /** Share of the starting solid still there (0–1). */
  solidLeftShare: number;
  acidLeftMol: number;
  /** True when the gas is more than the syringe holds; the syringe is drawn full and a note says so. */
  syringeFull: boolean;
  steps: WorkedStep[];
  limitNote: string | null;
}

/** Three significant figures, Australian formatting, without exponent notation. */
export function sig3(value: number): string {
  if (value === 0) return '0';
  return value.toLocaleString('en-AU', { maximumSignificantDigits: 3 });
}

function molarMassOf(partner: PartnerData): number {
  const result = calculateMolarMass(stripState(partner.species));
  if (!result.value) throw new Error(`No molar mass for ${partner.species}: ${result.error ?? 'unknown'}`);
  return result.value.molarMass;
}

/** Amounts, limiting reagent, gas volume and leftovers for one learner setup. Throws on invalid input. */
export function calculateAcidGas(reaction: AcidReaction, setup: AcidGasSetup): AcidGasResult {
  const error = firstModelError(ACID_GAS_FIELDS, setup);
  if (error) throw new Error(error);
  const gasTerm = reaction.products.find((term) => term.species === 'H2(g)' || term.species === 'CO2(g)');
  if (!gasTerm) throw new Error(`This reaction releases no gas: ${reaction.id}`);
  const gasFormula = gasTerm.species === 'H2(g)' ? 'H2' : 'CO2';
  const gasName = gasFormula === 'H2' ? 'hydrogen' : 'carbon dioxide';
  const gasLabel = formatSpecies(gasFormula);

  const partner = partnerById(reaction.partner);
  const acid = acidById(reaction.acid);
  const solidLabel = formatSpecies(stripState(partner.species));
  const acidLabel = formatSpecies(stripState(acid.species));
  const [solidCoefficient, acidCoefficient] = [reaction.reactants[0].coefficient, reaction.reactants[1].coefficient];
  const gasCoefficient = gasTerm.coefficient;

  const molarMassG = molarMassOf(partner);
  const solidMol = setup.massG / molarMassG;
  const volumeL = setup.volumeMl / 1000;
  const acidMol = setup.concentrationMolL * volumeL;
  const solidExtent = solidMol / solidCoefficient;
  const acidExtent = acidMol / acidCoefficient;
  const limiting: LimitingReagent = Math.abs(solidExtent - acidExtent) < 1e-12 ? 'both' : solidExtent < acidExtent ? 'solid' : 'acid';
  const extent = Math.min(solidExtent, acidExtent);
  const share = setup.progressPct / 100;
  const reacted = extent * share;
  const finished = share === 1;

  const gasMol = reacted * gasCoefficient;
  const gasMl = gasMol * SLC_MOLAR_VOLUME_L * 1000;
  const finalGasMl = extent * gasCoefficient * SLC_MOLAR_VOLUME_L * 1000;
  const solidLeftMol = Math.max(solidMol - solidCoefficient * reacted, 0);
  const acidLeftMol = Math.max(acidMol - acidCoefficient * reacted, 0);
  const solidLeftG = solidLeftMol * molarMassG;
  const syringeFull = gasMl > GAS_SYRINGE_CAPACITY_ML;

  const limitingName = limiting === 'solid' ? partner.name : limiting === 'acid' ? acid.name : 'both reactants';
  const limitingLabel = limiting === 'acid' ? acidLabel : solidLabel;
  const limitingMol = limiting === 'acid' ? acidMol : solidMol;
  const limitingCoefficient = limiting === 'acid' ? acidCoefficient : solidCoefficient;
  const gasStatus: WorkedStep['status'] = finished ? 'done' : 'current';

  const steps: WorkedStep[] = [
    {
      id: 'solid',
      title: `Amount of ${partner.name}`,
      formula: 'n = m ÷ M',
      working: `n(${solidLabel}) = ${sig3(setup.massG)} g ÷ ${formatModelNumber(molarMassG, 2)} g/mol = ${sig3(solidMol)} mol`,
      status: 'done',
    },
    {
      id: 'acid',
      title: `Amount of ${acid.name}`,
      formula: 'n = c × V',
      working: `n(${acidLabel}) = ${sig3(setup.concentrationMolL)} mol/L × ${sig3(volumeL)} L (${formatModelNumber(setup.volumeMl, 1)} mL) = ${sig3(acidMol)} mol`,
      status: 'done',
    },
    {
      id: 'ratio',
      title: 'Mole ratio from the balanced equation',
      formula: `${formatTerms(reaction.reactants)} → ${formatTerms(reaction.products)}`,
      working: `${solidCoefficient} ${solidLabel} : ${acidCoefficient} ${acidLabel} : ${gasCoefficient} ${gasLabel}`,
      status: 'done',
    },
    {
      id: 'limiting',
      title: 'Limiting reagent',
      formula: 'compare n ÷ coefficient',
      working: `${solidLabel}: ${sig3(solidMol)} ÷ ${solidCoefficient} = ${sig3(solidExtent)}; ${acidLabel}: ${sig3(acidMol)} ÷ ${acidCoefficient} = ${sig3(acidExtent)} → ${
        limiting === 'both' ? 'both run out together' : `${limitingName} runs out first (limiting)`
      }`,
      status: 'done',
    },
    {
      id: 'gas',
      title: `Amount of ${gasName}${finished ? '' : ' so far'}`,
      formula: `n(${gasLabel}) = n(limiting) × ${gasCoefficient}/${limitingCoefficient}${finished ? '' : ' × share reacted'}`,
      working: `n(${gasLabel}) = ${sig3(limitingMol)} mol ${limitingLabel} × ${gasCoefficient}/${limitingCoefficient}${
        finished ? '' : ` × ${formatModelNumber(setup.progressPct, 0)} %`
      } = ${sig3(gasMol)} mol`,
      status: gasStatus,
    },
    {
      id: 'volume',
      title: `Volume of ${gasName} at 25 °C, 100 kPa`,
      formula: `V = n × ${SLC_MOLAR_VOLUME_L} L/mol`,
      working: `V = ${sig3(gasMol)} mol × ${SLC_MOLAR_VOLUME_L} L/mol = ${sig3(gasMl / 1000)} L = ${sig3(gasMl)} mL`,
      status: gasStatus,
    },
    {
      id: 'leftover',
      title: finished ? 'What is left over' : 'What is left so far',
      formula: 'left = start − used',
      working: `${solidLabel}: ${sig3(solidLeftMol)} mol × ${formatModelNumber(molarMassG, 2)} g/mol = ${sig3(solidLeftG)} g left; ${acidLabel}: ${sig3(acidMol)} − ${acidCoefficient} × ${sig3(reacted)} = ${sig3(acidLeftMol)} mol left`,
      status: gasStatus,
    },
  ];

  const limitNote = syringeFull
    ? `${sig3(gasMl)} mL is more than the ${GAS_SYRINGE_CAPACITY_ML} mL gas syringe holds. The plunger stops at ${GAS_SYRINGE_CAPACITY_ML} mL; the model does not draw the extra gas. Use less solid or less acid to measure all of it.`
    : null;

  return {
    reaction,
    partner,
    gasFormula,
    gasName,
    molarMassG,
    solidMol,
    acidMol,
    limiting,
    gasMol,
    gasMl,
    finalGasMl,
    solidLeftG,
    solidLeftShare: solidMol === 0 ? 0 : solidLeftMol / solidMol,
    acidLeftMol,
    syringeFull,
    steps,
    limitNote,
  };
}

// ---------------------------------------------------------------------------------------------
// Teaching goal (CT-INTERACT C7): one question and three tasks checked on the result
// ---------------------------------------------------------------------------------------------

export interface AcidGasChallengeInput {
  setup: AcidGasSetup;
  result: AcidGasResult;
}

export const ACID_GAS_QUESTION = 'What decides how much gas a reaction makes?';

/** The syringe task accepts at least this much gas, without overflowing the syringe. */
export const FILL_SYRINGE_MIN_ML = 80;
/** "Run out together" accepts the two reactant amounts (÷ coefficient) within 5 % of each other. */
export const RUN_OUT_TOGETHER_TOLERANCE = 0.05;

function extents({ result }: AcidGasChallengeInput): { solid: number; acid: number } {
  const [solid, acid] = result.reaction.reactants;
  return { solid: result.solidMol / solid.coefficient, acid: result.acidMol / acid.coefficient };
}

/** Teaching tasks. Each check reads only the setup and the model result, never the UI. */
export const ACID_GAS_CHALLENGES: ExploreChallenge<AcidGasChallengeInput>[] = [
  {
    id: 'fill-syringe',
    prompt: `When the reaction has finished, collect ${FILL_SYRINGE_MIN_ML}–${GAS_SYRINGE_CAPACITY_ML} mL of gas without overflowing the syringe.`,
    hint: 'Keep the progress at 100 % and change the mass of solid in small steps.',
    isMet: ({ setup, result }) =>
      setup.progressPct === 100 && result.gasMl >= FILL_SYRINGE_MIN_ML && result.gasMl <= GAS_SYRINGE_CAPACITY_ML,
    why: `The gas comes from the reactant that runs out. With the acid in excess, 0.09 g of magnesium (0.0037 mol) gives 0.0037 mol × ${SLC_MOLAR_VOLUME_L} L/mol = 92 mL of hydrogen, but 0.10 g gives 102 mL and pushes past the syringe.`,
  },
  {
    id: 'more-acid-still-limiting',
    prompt: 'Make the acid the limiting reagent even though you have more moles of acid than of solid.',
    hint: 'Use hydrochloric acid with magnesium: the equation needs 2 HCl for every Mg. Try a dilute acid.',
    isMet: (input) => input.result.limiting === 'acid' && input.result.acidMol > input.result.solidMol,
    why: 'The reactant with fewer moles is not always the limiting one. Mg + 2HCl needs two HCl for each Mg, so 0.005 mol HCl reacts with only 0.0025 mol Mg: you must divide each amount by its coefficient before you compare.',
  },
  {
    id: 'run-out-together',
    prompt: 'Find an acid volume where the solid and the acid run out together (within 5 %).',
    hint: 'Set the acid to 0.1 mol/L, then change only the acid volume and watch the limiting-reagent step.',
    isMet: (input) => {
      const { solid, acid } = extents(input);
      return Math.abs(solid / acid - 1) <= RUN_OUT_TOGETHER_TOLERANCE;
    },
    why: '0.05 g of magnesium (0.00206 mol) needs 2 × 0.00206 = 0.00411 mol HCl, which is 41 mL of 0.1 mol/L acid. Below that volume more acid makes more gas; above it the extra acid is left over, so doubling the acid only helps while the acid is limiting.',
  },
];

// ---------------------------------------------------------------------------------------------
// 3D scene state, from the same result
// ---------------------------------------------------------------------------------------------

export type SolidForm = 'ribbon' | 'granules' | 'filings' | 'chips' | 'powder';

const SOLID_FORMS: Partial<Record<string, SolidForm>> = {
  Mg: 'ribbon',
  Zn: 'granules',
  Fe: 'filings',
  CaCO3: 'chips',
  Na2CO3: 'powder',
  NaHCO3: 'powder',
};

export interface AcidGasSceneState {
  solidForm: SolidForm;
  /** Metals are grey; carbonates are white. */
  solidTone: 'metal' | 'white';
  /** Number of pieces drawn; follows the mass. */
  pieces: number;
  /** Size of each piece as a share of its starting size (0–1). */
  solidLeftShare: number;
  acidVolumeMl: number;
  /** Gas in the syringe, at most the syringe capacity. */
  syringeMl: number;
  syringeFull: boolean;
  /** Bubbles show while the reaction is still going. */
  reacting: boolean;
  vigour: Vigour;
  /** Iron(II) salts turn the solution pale green as the reaction goes. */
  solutionTint: 'colourless' | 'pale-green';
  /** How far the solution colour has developed (0–1). */
  tintShare: number;
}

export const MAX_SOLID_PIECES = 12;

export function acidGasSceneState(result: AcidGasResult, setup: AcidGasSetup): AcidGasSceneState {
  const form = SOLID_FORMS[result.partner.id];
  if (!form) throw new Error(`No solid form for ${result.partner.id}`);
  const share = setup.progressPct / 100;
  return {
    solidForm: form,
    solidTone: result.partner.kind === 'metal' ? 'metal' : 'white',
    pieces: Math.min(MAX_SOLID_PIECES, Math.max(1, Math.ceil(setup.massG * 6))),
    solidLeftShare: result.solidLeftShare,
    acidVolumeMl: setup.volumeMl,
    syringeMl: Math.min(result.gasMl, GAS_SYRINGE_CAPACITY_ML),
    syringeFull: result.syringeFull,
    reacting: share > 0 && share < 1,
    vigour: result.reaction.vigour,
    solutionTint: result.partner.id === 'Fe' ? 'pale-green' : 'colourless',
    tintShare: result.partner.id === 'Fe' ? share : 0,
  };
}
